import { useEffect, useRef, useState } from "react";
import type { EditorSettings } from "./editorSettings";

export interface ViewMenuProps {
  settings: EditorSettings;
  onSettingsChange: (patch: Partial<EditorSettings>) => void;
  align?: "start" | "end";
}

export function ViewMenu({
  settings,
  onSettingsChange,
  align = "start",
}: ViewMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="sm-view-menu" ref={rootRef}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        className={open ? "is-active" : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        Visningsindstillinger
      </button>
      {open ? (
        <div
          className={`sm-view-menu-pop${align === "end" ? " is-end" : ""}`}
          role="menu"
        >
          <div
            className="sm-segmented"
            role="radiogroup"
            aria-label="Kolonner"
          >
            {(
              [
                ["visual-audio", "Billede · Lyd"],
                ["audio-visual", "Lyd · Billede"],
                ["audio-only", "Kun lyd"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={settings.scriptColumnOrder === value}
                className={
                  settings.scriptColumnOrder === value ? "is-active" : undefined
                }
                onClick={() =>
                  onSettingsChange({ scriptColumnOrder: value })
                }
              >
                {label}
              </button>
            ))}
          </div>
          <label className="sm-setting">
            <input
              type="checkbox"
              checked={settings.showSetupNumbers}
              onChange={(e) =>
                onSettingsChange({ showSetupNumbers: e.target.checked })
              }
            />
            <span>Indstillingsnumre</span>
          </label>
          <label className="sm-setting">
            <input
              type="checkbox"
              checked={settings.showCameraNotes}
              onChange={(e) =>
                onSettingsChange({ showCameraNotes: e.target.checked })
              }
            />
            <span>Kameranoter</span>
          </label>
          <label className="sm-setting">
            <input
              type="checkbox"
              checked={settings.showPosition}
              onChange={(e) =>
                onSettingsChange({ showPosition: e.target.checked })
              }
            />
            <span>Position</span>
          </label>
          <label className="sm-setting">
            <input
              type="checkbox"
              checked={settings.showComments}
              onChange={(e) =>
                onSettingsChange({ showComments: e.target.checked })
              }
            />
            <span>Kommentarer</span>
          </label>
          <div
            className="sm-segmented"
            role="radiogroup"
            aria-label="Vandrette streger"
          >
            {(
              [
                ["all", "Alle streger"],
                ["none", "Ingen"],
                ["custom", "Egne"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={settings.rowRuleMode === value}
                className={
                  settings.rowRuleMode === value ? "is-active" : undefined
                }
                onClick={() => onSettingsChange({ rowRuleMode: value })}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="sm-setting">
            <input
              type="checkbox"
              checked={settings.rowRuleHitInGutter}
              onChange={(e) =>
                onSettingsChange({ rowRuleHitInGutter: e.target.checked })
              }
            />
            <span>Streg-hover kun mellem kolonner</span>
          </label>
        </div>
      ) : null}
    </div>
  );
}
